<template>
  <div class="script-ide">
    <div class="script-ide-top">
      <CaseConfigHeader :header="script.name" :subheader="scriptType"></CaseConfigHeader>

      <v-layout row align-center class="script-toolbar mb-2" wrap>
      <v-btn :ripple="false" flat class="ma-0 text-link-button" @click="scriptDetailsToggled = !scriptDetailsToggled">
        <v-icon small color="secondary" class="mr-2">{{ scriptDetailsToggled ? 'visibility_off' : 'visibility' }}</v-icon>
        {{ scriptDetailsToggled ? 'Hide details' : 'Details' }}
      </v-btn>

      <span class="mx-3 body-1">
        Last saved {{ updated }} —
        <b v-if="isDirty" style="color:#e67e22">Unsaved</b>
        <b v-else-if="!isLive" style="color:#db4437">Not Live</b>
        <b v-else style="color:#0d9460">Live</b>
      </span>

      <v-select
        v-model="runtimePreset"
        :items="runtimePresets"
        label="Runtime"
        dense
        hide-details
        class="runtime-select mx-2"
        style="max-width: 200px;"
        @change="onRuntimePresetChange"
      ></v-select>
      <v-text-field
        v-if="runtimePreset === 'custom'"
        v-model="script.runtimeImage"
        label="Custom image"
        dense
        hide-details
        class="mx-2"
        style="max-width: 320px;"
        placeholder="caseblocks/cb-runtime-shopify:1.0.0"
      ></v-text-field>

      <v-spacer></v-spacer>

      <span class="caption grey--text mr-2 hidden-sm-and-down">⌘/Ctrl+S Save · ⌘/Ctrl+Enter Run</span>
      <span v-if="running && executionStatusLabel" class="caption mr-2 script-run-status" :class="executionStatusClass">{{ executionStatusLabel }}</span>
      <v-btn class="mr-1" color="secondary" :loading="saving" :disabled="busy" @click="saveScript">Save</v-btn>
      <v-btn class="mr-1" color="secondary" :loading="running" :disabled="busy" @click="runScript">Run</v-btn>
      <v-btn class="mr-1" color="success" :disabled="busy || isLive" @click="makeLive">Make Live</v-btn>
      <v-btn class="mr-1" color="warning" outline :disabled="busy || isLive" @click="confirmRevert = true">Revert</v-btn>
      <v-btn color="secondary" outline @click="returnToConfig">Back</v-btn>
      </v-layout>

      <v-slide-y-transition>
        <v-layout v-if="scriptDetailsToggled" row wrap class="mb-2 script-details" style="background:#fff; padding: 8px 12px;">
          <v-flex xs12 sm6>
            <v-text-field label="Script name" v-model="script.name" box></v-text-field>
            <v-textarea box v-model="script.description" label="Script description" class="mt-2"></v-textarea>
            <v-switch color="secondary" v-model="script.reusable" label="Library function"></v-switch>
          </v-flex>
        </v-layout>
      </v-slide-y-transition>
    </div>

    <div class="script-body" ref="scriptBody" :class="{ 'is-resizing': resizing }">
      <div class="editor-col">
        <edit-code-pane
          ref="mainEditor"
          :code="script.draftCode"
          :language="editorLanguage"
          :script-sdk-dialect="scriptSdkDialect"
          height="100%"
          v-on:update-code="updateCode"
        ></edit-code-pane>
      </div>

      <div
        class="split-handle"
        :class="{ 'is-dragging': resizing }"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize editor and log panels"
        @mousedown.prevent="startResize"
      ></div>

      <div class="side-col" :style="sideColStyle">
        <v-tabs v-model="sideTab" slider-color="secondary" class="side-tabs">
          <v-tab>Log</v-tab>
          <v-tab>Input</v-tab>
          <v-tab>Output</v-tab>
        </v-tabs>

        <div v-show="sideTab === 0" class="side-pane">
          <LogViewer
            :log-entries="logEntries"
            :execution-meta="executionMeta"
            class="log-viewer-fill"
            @clear-logs="clearLogs"
          ></LogViewer>
        </div>

        <div v-show="sideTab === 1" class="side-pane">
          <v-autocomplete
            v-model="selectedCase"
            :items="caseSearchItems"
            :search-input.sync="caseSearchTerm"
            :loading="caseSearching"
            label="Search input case"
            dense
            box
            clearable
            hide-no-data
            no-filter
            item-text="text"
            item-value="value"
            return-object
            class="mb-2 case-search"
            @change="onCaseSelected"
          ></v-autocomplete>
          <edit-code-pane
            ref="inputEditor"
            :code="script.draftDocument"
            language="json"
            height="100%"
            v-on:update-code="updateCodeValueInput"
          ></edit-code-pane>
        </div>

        <div v-show="sideTab === 2" class="side-pane">
          <edit-code-pane
            ref="outputEditor"
            :code="codeValueOutput"
            language="json"
            height="100%"
            :read-only="true"
          ></edit-code-pane>
        </div>
      </div>
    </div>

    <div class="script-ide-footer">
      <v-btn color="error" @click="deleteDialog = true" dark class="ml-0">
        <v-icon>delete</v-icon>&emsp;Delete Script
      </v-btn>
      <delete-dialog :isOpen="deleteDialog" itemType="script" v-on:delete-item="deleteScript"></delete-dialog>
    </div>

    <v-dialog v-model="confirmRevert" max-width="420">
      <v-card>
        <v-card-title class="headline">Revert to live?</v-card-title>
        <v-card-text>Replace draft with the published (live) code. Unsaved draft changes will be lost.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="confirmRevert = false">Cancel</v-btn>
          <v-btn color="warning" @click="revertToLive">Revert</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import EditCodePane from "@/components/shared/editCodePane.vue";
import DeleteDialog from "@/components/shared/deleteDialog";
import Helpers from "@/helpers.js";
import CaseConfigHeader from "@/components/cases/case-config/CaseConfigHeader.vue";
import LogViewer from "@/components/cases/case-config/scripts/LogViewer.vue";
import { getCaseSearch } from "@/provider/cases.js";
import { applySampleIfSafe, isKnownSample, sampleForRuntime } from "@/components/cases/case-config/scripts/scriptSamples.js";
import { resolveScriptSdkDialect } from "@/components/cases/case-config/scripts/scriptSdkCompletions.js";
import { logFeature } from "@/helpers/featureActivity";

var RUNTIME_PRESETS = [
  { text: "JavaScript", value: "javascript" },
  { text: "Python", value: "python" },
  { text: "Ruby", value: "ruby" },
  { text: "Go", value: "go" },
  { text: "Rust", value: "rust" },
  { text: "Agentic", value: "agentic" },
  { text: "Expressive5", value: "expressive5" },
  { text: "Custom image…", value: "custom" },
];

var FAAS_LANG_FOR_PRESET = {
  javascript: "javascript",
  python: "python",
  ruby: "ruby",
  go: "go",
  rust: "rust",
  agentic: "agent",
};

var COMPILED_FAAS_LANGUAGES = {
  go: true,
  golang: true,
  rust: true,
  java: true,
  dotnet: true,
  csharp: true,
};

function newScriptRunId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "run-" + Date.now() + "-" + Math.random().toString(16).slice(2);
}

function isCompiledScriptLanguage(language) {
  if (!language) return false;
  return !!COMPILED_FAAS_LANGUAGES[String(language).toLowerCase()];
}

export default {
  name: "script-content",
  components: { EditCodePane, DeleteDialog, CaseConfigHeader, LogViewer },
  props: {
    script: { type: Object },
  },
  data: function () {
    return {
      deleteDialog: false,
      confirmRevert: false,
      scriptDetailsToggled: false,
      selectedCase: null,
      codeValueOutput: "",
      logEntries: [],
      executionMeta: null,
      sideTab: 0,
      saving: false,
      running: false,
      lastSavedDraft: "",
      runtimePreset: "javascript",
      runtimePresets: RUNTIME_PRESETS,
      caseSearchTerm: "",
      caseSearchItems: [],
      caseSearching: false,
      caseSearchTimer: null,
      lifeCycleNames: ["precreate", "postcreate", "presave", "postsave", "predelete", "postdelete"],
      sideWidthPercent: 32,
      resizing: false,
      phasePollTimer: null,
      elapsedTimer: null,
      runStartedAt: null,
    };
  },
  computed: {
    ...mapState({
      caseType: function (state) {
        return state.caseTypes.current;
      },
      caseTypeId: function (state) {
        return state.cbroute.params.case_type_id;
      },
      timePref: function (state) {
        if (
          state &&
          state.currentUser &&
          state.currentUser.me &&
          state.currentUser.me.preferences &&
          state.currentUser.me.preferences.dateTimeFormat
        ) {
          return state.currentUser.me.preferences.dateTimeFormat;
        }
        return "HH:MM, D MMM";
      },
    }),
    updated: function () {
      return this.formatDate(this.script.updatedAt, this.timePref);
    },
    isLifeCycle: function () {
      return this.lifeCycleNames.indexOf((this.script.name || "").toLowerCase()) !== -1;
    },
    scriptType: function () {
      return this.isLifeCycle ? "Lifecycle" : "Script";
    },
    isLive: function () {
      return this.script.draftCode === this.script.publishedCode;
    },
    isDirty: function () {
      return this.script.draftCode !== this.lastSavedDraft;
    },
    busy: function () {
      return this.saving || this.running;
    },
    editorLanguage: function () {
      if (this.runtimePreset === "custom") {
        return this.script.runtimeLanguage || "javascript";
      }
      if (this.runtimePreset === "expressive5") return "javascript";
      if (this.runtimePreset === "agentic") return "plaintext";
      return this.runtimePreset || "javascript";
    },
    scriptSdkDialect: function () {
      var lang = this.script && this.script.runtimeLanguage;
      return resolveScriptSdkDialect(this.runtimePreset, lang) || "";
    },
    sideColStyle: function () {
      return {
        flex: "0 0 " + this.sideWidthPercent + "%",
        width: this.sideWidthPercent + "%",
        maxWidth: "none",
      };
    },
    initialRunStatus: function () {
      var lang = this.script && this.script.runtimeLanguage;
      if (this.runtimePreset === "go" || this.runtimePreset === "rust") return "compiling";
      if (this.script && this.script.runtimeType === "faas" && isCompiledScriptLanguage(lang)) {
        return "compiling";
      }
      return "running";
    },
    executionStatusLabel: function () {
      if (!this.executionMeta || !this.executionMeta.status) return "";
      var s = String(this.executionMeta.status).toLowerCase();
      if (s === "compiling") return "Compiling";
      if (s === "running") return "Running";
      return "";
    },
    executionStatusClass: function () {
      if (!this.executionMeta || !this.executionMeta.status) return "";
      var s = String(this.executionMeta.status).toLowerCase();
      if (s === "compiling") return "is-compiling";
      if (s === "running") return "is-running";
      return "";
    },
  },
  watch: {
    caseSearchTerm: function (term) {
      this.scheduleCaseSearch(term);
    },
    script: {
      immediate: true,
      handler: function (s) {
        if (!s) return;
        this.syncRuntimePresetFromScript();
        this.lastSavedDraft = s.draftCode || "";
      },
    },
  },
  mounted: function () {
    window.addEventListener("keydown", this.onKeydown);
    this.syncRuntimePresetFromScript();
    this.lastSavedDraft = this.script.draftCode || "";
    var self = this;
    this.$nextTick(function () {
      if (self.$refs.mainEditor && self.$refs.mainEditor.layout) {
        self.$refs.mainEditor.layout();
      }
    });
  },
  beforeDestroy: function () {
    window.removeEventListener("keydown", this.onKeydown);
    this.stopResize();
    if (this.caseSearchTimer) clearTimeout(this.caseSearchTimer);
    this.stopRunProgress();
  },
  methods: {
    startResize: function (e) {
      this.resizing = true;
      document.addEventListener("mousemove", this.onResizeMove);
      document.addEventListener("mouseup", this.stopResize);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      this.onResizeMove(e);
    },
    onResizeMove: function (e) {
      if (!this.resizing) return;
      var body = this.$refs.scriptBody;
      if (!body) return;
      var rect = body.getBoundingClientRect();
      if (!rect.width) return;
      var handleWidth = 8;
      var minSidePx = 220;
      var minEditorPx = 240;
      var sidePx = rect.right - e.clientX;
      var maxSidePx = rect.width - minEditorPx - handleWidth;
      if (sidePx < minSidePx) sidePx = minSidePx;
      if (sidePx > maxSidePx) sidePx = maxSidePx;
      this.sideWidthPercent = (sidePx / rect.width) * 100;
      this.relayoutEditors();
    },
    stopResize: function () {
      if (!this.resizing) {
        document.removeEventListener("mousemove", this.onResizeMove);
        document.removeEventListener("mouseup", this.stopResize);
        return;
      }
      this.resizing = false;
      document.removeEventListener("mousemove", this.onResizeMove);
      document.removeEventListener("mouseup", this.stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      this.relayoutEditors();
    },
    relayoutEditors: function () {
      var self = this;
      this.$nextTick(function () {
        var refs = [self.$refs.mainEditor, self.$refs.inputEditor, self.$refs.outputEditor];
        for (var i = 0; i < refs.length; i++) {
          if (refs[i] && refs[i].layout) refs[i].layout();
        }
      });
    },
    onKeydown: function (e) {
      var mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        this.saveScript();
      } else if (e.key === "Enter") {
        e.preventDefault();
        this.runScript();
      }
    },
    syncRuntimePresetFromScript: function () {
      var t = this.script.runtimeType;
      var lang = this.script.runtimeLanguage;
      if (lang === "agent" || lang === "agentic") {
        this.runtimePreset = "agentic";
      } else if (!t || t === "exp5" || t === "expressive5") {
        this.runtimePreset = "expressive5";
      } else if (lang === "go" || lang === "golang") {
        this.runtimePreset = "go";
      } else if (lang === "typescript") {
        this.runtimePreset = "javascript";
      } else if ((t === "faas" || t === "docker") && lang && FAAS_LANG_FOR_PRESET[lang]) {
        this.runtimePreset = lang;
      } else if (this.script.runtimeImage && t === "docker") {
        this.runtimePreset = "custom";
      } else {
        this.runtimePreset = lang || "javascript";
      }
      this.script.draftCode = applySampleIfSafe(this.script.draftCode, this.runtimePreset);
      if (isKnownSample(this.script.publishedCode)) {
        this.script.publishedCode = sampleForRuntime(this.runtimePreset);
      }
    },
    onRuntimePresetChange: function () {
      var preset = this.runtimePreset;
      var nextSample = sampleForRuntime(preset);
      if (preset === "custom") {
        this.script.runtimeType = "docker";
        if (!this.script.runtimeImage) this.script.runtimeImage = "";
        this.script.draftCode = applySampleIfSafe(this.script.draftCode, "custom");
        if (isKnownSample(this.script.publishedCode)) {
          this.script.publishedCode = nextSample;
        }
        return;
      }
      if (preset === "expressive5") {
        this.script.runtimeType = "exp5";
        this.script.runtimeLanguage = "javascript";
        this.script.runtimeImage = null;
        this.script.draftCode = applySampleIfSafe(this.script.draftCode, "expressive5");
        if (isKnownSample(this.script.publishedCode)) {
          this.script.publishedCode = nextSample;
        }
        return;
      }
      this.script.runtimeType = "faas";
      this.script.runtimeLanguage = FAAS_LANG_FOR_PRESET[preset] || preset;
      this.script.runtimeImage = null;
      this.script.draftCode = applySampleIfSafe(this.script.draftCode, preset);
      if (isKnownSample(this.script.publishedCode)) {
        this.script.publishedCode = nextSample;
      }
    },
    scheduleCaseSearch: function (term) {
      var self = this;
      if (this.caseSearchTimer) clearTimeout(this.caseSearchTimer);
      if (!term || term.length < 1) {
        this.caseSearchItems = [];
        return;
      }
      this.caseSearchTimer = setTimeout(function () {
        self.runCaseSearch(term);
      }, 300);
    },
    runCaseSearch: async function (term) {
      if (!this.caseType || !this.caseType.code) return;
      this.caseSearching = true;
      try {
        var rows = await getCaseSearch(this.caseType.code, 0, 25, term, ["title", "properties"]);
        this.caseSearchItems = (rows || []).map(function (r) {
          return {
            text: (r.title || r._id || "case") + "",
            value: r,
          };
        });
      } catch (err) {
        console.error(err);
        this.caseSearchItems = [];
      }
      this.caseSearching = false;
    },
    onCaseSelected: function () {
      if (!this.selectedCase || !this.selectedCase.value) return;
      var row = this.selectedCase.value;
      var doc = Object.assign({}, row.properties || {});
      if (row._id) doc.id = row._id;
      if (row.title) doc.title = row.title;
      this.script.draftDocument = JSON.stringify(doc, null, 2);
      this.sideTab = 1;
    },
    returnToConfig: function () {
      this.$router.push({
        name: "CaseConfig",
        params: { case_type_id: this.caseTypeId, config_tab: "Scripts" },
      });
    },
    deleteScript: async function (shouldDelete) {
      this.deleteDialog = false;
      if (shouldDelete) {
        await this.$store.dispatch("deleteScript", {
          scriptId: this.script.id,
          caseTypeId: this.caseTypeId,
        });
        this.returnToConfig();
      }
    },
    formatDate: function (datetime) {
      if (!datetime) return "never";
      var date = Helpers.formatEventDateSimple(datetime);
      var time = new Date(datetime).toLocaleTimeString().split(":");
      return date + " @ " + time[0] + ":" + time[1];
    },
    updateCode: function (update) {
      this.script.draftCode = update;
    },
    updateCodeValueInput: function (update) {
      this.script.draftDocument = update;
    },
    saveScript: async function () {
      this.saving = true;
      try {
        var result = await this.$store.dispatch("updateScript", this.script);
        if (!result.error) {
          this.script.updatedAt = result.updatedAt;
          if (result.runtimeType !== undefined) this.script.runtimeType = result.runtimeType;
          if (result.runtimeLanguage !== undefined) this.script.runtimeLanguage = result.runtimeLanguage;
          if (result.runtimeImage !== undefined) this.script.runtimeImage = result.runtimeImage;
          this.syncRuntimePresetFromScript();
          this.lastSavedDraft = this.script.draftCode;
        } else {
          this.pushLog("error", result.message || "Save failed");
        }
      } catch (err) {
        this.pushLog("error", err.message || String(err));
      }
      this.saving = false;
    },
    stopRunProgress: function () {
      if (this.phasePollTimer) {
        clearInterval(this.phasePollTimer);
        this.phasePollTimer = null;
      }
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },
    setInFlightMeta: function (status) {
      var elapsed = this.runStartedAt ? Date.now() - this.runStartedAt : 0;
      this.executionMeta = {
        status: status,
        responseTime: elapsed,
        meta: null,
        failureMessage: null,
        error: false,
        message: null,
      };
    },
    startRunProgress: function (runId) {
      var self = this;
      this.stopRunProgress();
      this.elapsedTimer = setInterval(function () {
        if (!self.running || !self.executionMeta) return;
        var status = self.executionMeta.status;
        if (status !== "compiling" && status !== "running") return;
        self.setInFlightMeta(status);
      }, 250);
      if (!runId) return;
      this.phasePollTimer = setInterval(function () {
        self.pollExecutionPhase(runId);
      }, 150);
      this.pollExecutionPhase(runId);
    },
    mapLogEntries: function (logs) {
      return (logs || []).map(function (l) {
        return {
          message: l.message,
          level: (l.level || "info").toLowerCase(),
          timestamp: l.timestamp || new Date().toISOString(),
        };
      });
    },
    mergeLogEntries: function (logs) {
      var incoming = this.mapLogEntries(logs);
      var current = this.logEntries || [];
      var seen = {};
      var merged = current.slice();
      var i;
      for (i = 0; i < current.length; i++) {
        seen[current[i].timestamp + "\n" + current[i].level + "\n" + current[i].message] = true;
      }
      for (i = 0; i < incoming.length; i++) {
        var entry = incoming[i];
        var key = entry.timestamp + "\n" + entry.level + "\n" + entry.message;
        if (seen[key]) continue;
        seen[key] = true;
        merged.push(entry);
      }
      this.logEntries = merged;
    },
    applyExecutionResult: function (result) {
      var body = result || {};
      this.executionMeta = {
        status: body.status || (body.error || body.failureMessage ? "failure" : "success"),
        responseTime: body.responseTime,
        meta: body.meta || null,
        failureMessage: body.failureMessage || null,
        error: !!body.error,
        message: body.message || null,
      };
      if (body.log && body.log.length) {
        this.mergeLogEntries(body.log);
      } else if (!(this.logEntries && this.logEntries.length)) {
        this.logEntries = [];
      }
      if (body.error || body.failureMessage) {
        this.appendFailureLogs(
          body.failureMessage || body.message || body.lowLevelErrorMessage || "Execution error"
        );
        logFeature("script-ide", "run-failed", "Script execution failed", {
          name: this.script && this.script.name,
        });
      } else if (!(this.logEntries && this.logEntries.length)) {
        this.logEntries = [
          {
            message: "Script executed.",
            level: "info",
            timestamp: new Date().toISOString(),
          },
        ];
        logFeature("script-ide", "run", "Script executed", {
          name: this.script && this.script.name,
        });
      } else {
        logFeature("script-ide", "run", "Script executed", {
          name: this.script && this.script.name,
        });
      }
      this.codeValueOutput = body.output || "";
      this.stopRunProgress();
      this.running = false;
    },
    pollExecutionPhase: async function (runId) {
      if (!this.running || !runId) return;
      try {
        var progress = await this.$store.dispatch("getScriptExecutionPhase", runId);
        if (!this.running || !this.executionMeta) return;
        var phase = null;
        var logs = null;
        if (typeof progress === "string") {
          phase = progress;
        } else if (progress) {
          phase = progress.phase;
          logs = progress.log;
        }
        if (phase === "success" || phase === "failure") {
          this.applyExecutionResult({
            status: phase,
            responseTime: progress.responseTime,
            meta: progress.meta,
            failureMessage: progress.failureMessage,
            error: !!(progress.error || phase === "failure"),
            message: progress.message,
            log: logs,
            output: progress.output,
          });
          return;
        }
        if (phase === "compiling" || phase === "running") {
          this.setInFlightMeta(phase);
        }
        if (logs && logs.length) {
          this.mergeLogEntries(logs);
        }
      } catch (err) {
        // Older APIs without scriptExecutionPhase should not break Run.
      }
    },
    runScript: async function () {
      this.running = true;
      this.sideTab = 0;
      this.logEntries = [];
      this.runStartedAt = Date.now();
      var runId = newScriptRunId();
      this.setInFlightMeta(this.initialRunStatus);
      this.startRunProgress(runId);
      logFeature("script-ide", "run", "Running script from IDE", {
        name: this.script && this.script.name,
      });
      try {
        await this.saveScript();
        var payload = {};
        try {
          payload = JSON.parse(this.script.draftDocument || "{}");
        } catch (parseErr) {
          this.pushLog("error", "Invalid input JSON: " + parseErr.message);
          this.executionMeta = {
            status: "failure",
            responseTime: null,
            meta: null,
            failureMessage: parseErr.message,
            error: true,
            message: parseErr.message,
          };
          logFeature("script-ide", "run-failed", "Invalid input JSON", {
            name: this.script && this.script.name,
          });
          this.stopRunProgress();
          this.running = false;
          return;
        }
        var result = await this.$store.dispatch("executeScript", {
          id: this.script.id,
          payload: payload,
          runId: runId,
        });
        if (!this.running) return;
        if (result && result.status === "running") return;
        this.applyExecutionResult(result);
        return;
      } catch (err) {
        if (!this.running) return;
        var failText = this.graphqlErrorMessage(err);
        this.executionMeta = {
          status: "failure",
          responseTime: null,
          meta: null,
          failureMessage: failText,
          error: true,
          message: failText,
        };
        this.appendFailureLogs(failText);
        logFeature("script-ide", "run-failed", "Script execution threw", {
          name: this.script && this.script.name,
        });
      }
      this.stopRunProgress();
      this.running = false;
    },
    clearLogs: function () {
      this.logEntries = [];
      this.executionMeta = null;
    },
    pushLog: function (level, message) {
      this.logEntries = (this.logEntries || []).concat([
        { level: level, message: message, timestamp: new Date().toISOString() },
      ]);
    },
    appendFailureLogs: function (message) {
      var text = message == null ? "" : String(message);
      var lines = text.split(/\r?\n/);
      var existing = {};
      var current = this.logEntries || [];
      for (var i = 0; i < current.length; i++) {
        existing[current[i].message] = true;
      }
      var added = false;
      var hasLine = false;
      for (var j = 0; j < lines.length; j++) {
        if (!lines[j].length) continue;
        hasLine = true;
        if (existing[lines[j]]) continue;
        this.pushLog("error", lines[j]);
        existing[lines[j]] = true;
        added = true;
      }
      if (!added && !hasLine && text.trim() && !existing[text]) {
        this.pushLog("error", text);
      }
    },
    graphqlErrorMessage: function (err) {
      if (!err) return "Execution failed";
      var graphQLErrors = err.graphQLErrors;
      if (graphQLErrors && graphQLErrors[0] && graphQLErrors[0].message) {
        return graphQLErrors[0].message;
      }
      var networkError = err.networkError;
      if (networkError) {
        var result = networkError.result;
        if (result && result.errors && result.errors[0] && result.errors[0].message) {
          return result.errors[0].message;
        }
        if (networkError.message) return networkError.message;
      }
      return err.message || String(err);
    },
    makeLive: async function () {
      this.script.publishedCode = this.script.draftCode;
      await this.saveScript();
    },
    revertToLive: async function () {
      this.confirmRevert = false;
      this.script.draftCode = this.script.publishedCode;
      await this.saveScript();
    },
  },
};
</script>

<style scoped>
/* AppContainer uses .offset-top { margin-top: 110px } (50px toolbar + 60px header band) */
.script-ide {
  background: #f5f5f5;
  box-sizing: border-box;
  height: calc(100vh - 110px);
  max-height: calc(100vh - 110px);
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.script-ide-top {
  flex: 0 0 auto;
}
.script-toolbar {
  background: #fff;
  padding: 8px 4px;
  border-radius: 2px;
}
.script-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}
.editor-col {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.split-handle {
  flex: 0 0 8px;
  cursor: col-resize;
  position: relative;
  z-index: 2;
  align-self: stretch;
  background: transparent;
}
.split-handle::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -4px;
  right: -4px;
}
.split-handle::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 3px;
  width: 2px;
  background: #e0e0e0;
  border-radius: 1px;
}
.split-handle:hover::after,
.split-handle.is-dragging::after {
  background: #9e9e9e;
}
.script-body.is-resizing .editor-col,
.script-body.is-resizing .side-col {
  pointer-events: none;
  user-select: none;
}
.side-col {
  min-width: 220px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.side-tabs {
  flex: 0 0 auto;
}
.side-pane {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 4px;
  overflow: hidden;
}
.case-search {
  flex: 0 0 auto;
}
.log-viewer-fill {
  flex: 1 1 auto;
  min-height: 0;
}
.script-ide-footer {
  flex: 0 0 auto;
  padding-top: 8px;
}
.editor-col >>> .monaco-editor-frame,
.side-pane >>> .monaco-editor-frame {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  width: 100%;
}
.script-run-status {
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.script-run-status.is-compiling {
  color: #ef6c00;
}
.script-run-status.is-running {
  color: #1565c0;
}
</style>
